"use client";

import { c as _c } from "react/compiler-runtime";
import { AuthContext } from "@/components/auth/Auth";
import { useCookieConsent } from "@/components/cookies/CookieConsentContext";
import useCapacitor from "@/hooks/useCapacitor";
import { faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useParams, usePathname, useRouter, useSearchParams } from "next/navigation";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import UserPageTab from "./UserPageTab";
import { DEFAULT_USER_PAGE_TAB, USER_PAGE_TABS, type UserPageTabConfig, type UserPageTabKey, type UserPageVisibilityContext, getUserPageTabByRoute } from "./userTabs.config";
const DEFAULT_TAB = DEFAULT_USER_PAGE_TAB;

// Normalize consent country to uppercase code; empty or non-strings become null.
const normalizeCountry = (country: string | null | undefined): string | null => {
  if (typeof country !== "string") {
    return null;
  }
  const trimmed = country.trim();
  return trimmed ? trimmed.toUpperCase() : null;
};
const getVisibilityContext = ({
  showWaves,
  capacitorIsIos,
  country
}: {
  readonly showWaves: boolean;
  readonly capacitorIsIos: boolean;
  readonly country: string | null | undefined;
}): UserPageVisibilityContext => {
  const normalizedCountry = normalizeCountry(country);
  return {
    showWaves,
    hideSubscriptions: capacitorIsIos && normalizedCountry !== "US"
  };
};
const resolveTabFromPath = (pathname: string): UserPageTabKey => {
  const segments = pathname.split("/").filter(Boolean);
  const routeSegment = segments[1] ?? "";
  const match = getUserPageTabByRoute(routeSegment);
  return match?.id ?? DEFAULT_TAB;
};
const filterVisibleTabs = (tabs: readonly UserPageTabConfig[], context: UserPageVisibilityContext) => tabs.filter(tab => tab.isVisible ? tab.isVisible(context) : true);
export default function UserPageTabs() {
  const $ = _c(54);
  const pathname = usePathname() ?? "";
  const router = useRouter();
  const params = useParams();
  let t0;
  if ($[0] !== params?.user) {
    t0 = params?.user?.toString() ?? "";
    $[0] = params?.user;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const handleOrWallet = t0;
  const searchParams = useSearchParams();
  let t1;
  if ($[2] !== searchParams) {
    t1 = searchParams?.toString() ?? "";
    $[2] = searchParams;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const searchString = t1;
  const capacitor = useCapacitor();
  const {
    country
  } = useCookieConsent();
  const {
    showWaves
  } = useContext(AuthContext);
  let t2;
  if ($[4] !== capacitor.isIos || $[5] !== country || $[6] !== showWaves) {
    t2 = getVisibilityContext({
      showWaves,
      capacitorIsIos: capacitor.isIos,
      country
    });
    $[4] = capacitor.isIos;
    $[5] = country;
    $[6] = showWaves;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const visibilityContext = t2;
  const scrollContainerRef = useRef(null);
  const contentContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  let t3;
  if ($[8] !== pathname) {
    t3 = resolveTabFromPath(pathname);
    $[8] = pathname;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const resolvedTabFromPath = t3;
  let t4;
  if ($[10] !== visibilityContext) {
    t4 = filterVisibleTabs(USER_PAGE_TABS, visibilityContext);
    $[10] = visibilityContext;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const visibleTabs = t4;
  let t5;
  if ($[12] !== resolvedTabFromPath || $[13] !== visibleTabs) {
    let t6;
    if ($[15] !== resolvedTabFromPath) {
      t6 = tab => tab.id === resolvedTabFromPath;
      $[15] = resolvedTabFromPath;
      $[16] = t6;
    } else {
      t6 = $[16];
    }
    t5 = visibleTabs.some(t6);
    $[12] = resolvedTabFromPath;
    $[13] = visibleTabs;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const resolvedTabIsVisible = t5;
  let t6;
  bb0: {
    if (resolvedTabIsVisible) {
      t6 = resolvedTabFromPath;
      break bb0;
    }
    const firstVisibleTab = visibleTabs[0]?.id;
    t6 = firstVisibleTab ?? DEFAULT_TAB;
  }
  const activeTab = t6;
  let t7;
  if ($[17] !== handleOrWallet || $[18] !== pathname || $[19] !== resolvedTabIsVisible || $[20] !== router || $[21] !== searchString || $[22] !== visibleTabs[0] || $[23] !== visibleTabs.length) {
    t7 = () => {
      if (!visibleTabs.length || resolvedTabIsVisible || !handleOrWallet) {
        return;
      }
      const fallbackTab = visibleTabs[0];
      if (!fallbackTab) {
        return;
      }
      const fallbackPath = fallbackTab.route ? `/${handleOrWallet}/${fallbackTab.route}` : `/${handleOrWallet}`;
      if (fallbackPath === pathname) {
        return;
      }
      const nextUrl = searchString ? `${fallbackPath}?${searchString}` : fallbackPath;
      router.replace(nextUrl);
    };
    $[17] = handleOrWallet;
    $[18] = pathname;
    $[19] = resolvedTabIsVisible;
    $[20] = router;
    $[21] = searchString;
    $[22] = visibleTabs[0];
    $[23] = visibleTabs.length;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] !== handleOrWallet || $[26] !== pathname || $[27] !== resolvedTabIsVisible || $[28] !== router || $[29] !== searchString || $[30] !== visibleTabs) {
    t8 = [handleOrWallet, pathname, resolvedTabIsVisible, router, searchString, visibleTabs];
    $[25] = handleOrWallet;
    $[26] = pathname;
    $[27] = resolvedTabIsVisible;
    $[28] = router;
    $[29] = searchString;
    $[30] = visibleTabs;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  useEffect(t7, t8);
  let t9;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      const container = scrollContainerRef.current;
      if (!container) {
        return;
      }
      const {
        scrollLeft,
        scrollWidth,
        clientWidth
      } = container;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    };
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  const checkScroll = t9;
  let t10;
  let t11;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      const container_0 = scrollContainerRef.current;
      const contentContainer = contentContainerRef.current;
      if (!container_0) {
        return;
      }
      checkScroll();
      container_0.addEventListener("scroll", checkScroll);
      window.addEventListener("resize", checkScroll);
      let resizeObserver = null;
      if (typeof ResizeObserver !== "undefined") {
        resizeObserver = new ResizeObserver(() => {
          checkScroll();
        });
        resizeObserver.observe(container_0);
        if (contentContainer) {
          resizeObserver.observe(contentContainer);
        }
      }
      return () => {
        container_0.removeEventListener("scroll", checkScroll);
        window.removeEventListener("resize", checkScroll);
        resizeObserver?.disconnect();
      };
    };
    t11 = [checkScroll];
    $[33] = t10;
    $[34] = t11;
  } else {
    t10 = $[33];
    t11 = $[34];
  }
  useEffect(t10, t11);
  let t12;
  if ($[35] !== visibleTabs.length) {
    t12 = () => {
      if (!visibleTabs.length) {
        return;
      }
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          checkScroll();
        });
      });
    };
    $[35] = visibleTabs.length;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] !== visibleTabs) {
    t13 = [checkScroll, visibleTabs];
    $[37] = visibleTabs;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  useEffect(t12, t13);
  let t14;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => {
      const container_1 = scrollContainerRef.current;
      if (!container_1) {
        return;
      }
      container_1.scrollBy({
        left: -150,
        behavior: "smooth"
      });
    };
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  const scrollLeft_0 = t14;
  let t15;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => {
      const container_2 = scrollContainerRef.current;
      if (!container_2) {
        return;
      }
      container_2.scrollBy({
        left: 150,
        behavior: "smooth"
      });
    };
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  const scrollRight = t15;
  let t16;
  if ($[41] !== activeTab || $[42] !== visibleTabs) {
    t16 = visibleTabs.map(tabConfig => <UserPageTab key={tabConfig.id} tab={tabConfig} activeTabId={activeTab} parentRef={scrollContainerRef} />);
    $[41] = activeTab;
    $[42] = visibleTabs;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  let t17;
  if ($[44] !== t16) {
    t17 = <div ref={scrollContainerRef} className="tw-w-full tw-overflow-x-auto tw-overflow-y-hidden [touch-action:pan-x] [&::-webkit-scrollbar]:tw-hidden [-ms-overflow-style:none] [scrollbar-width:none]" aria-label="Tabs"><div ref={contentContainerRef} className="-tw-mb-px tw-flex tw-gap-x-3 lg:tw-gap-x-4 tw-min-w-max" aria-label="Tabs">{t16}</div></div>;
    $[44] = t16;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] !== canScrollLeft) {
    t18 = canScrollLeft && <><div className="tw-absolute tw-left-0 tw-top-0 tw-bottom-0 tw-w-24 tw-pointer-events-none tw-z-10 tw-bg-gradient-to-r tw-from-black tw-via-black/40 tw-to-black/0" /><button onClick={scrollLeft_0} aria-label="Scroll tabs left" className="tw-absolute tw-left-0 tw-top-1/2 tw--translate-y-1/2 tw-z-20 tw-inline-flex tw-items-center tw-justify-start tw-group tw-p-0 tw-h-10 tw-w-10 tw-bg-transparent tw-border-none tw-outline-none"><FontAwesomeIcon icon={faChevronLeft} className="tw-h-6 tw-w-6 tw-text-iron-200 group-hover:tw-text-iron-300 tw-transition tw-duration-300 tw-ease-out" /></button></>;
    $[46] = canScrollLeft;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  let t19;
  if ($[48] !== canScrollRight) {
    t19 = canScrollRight && <><div className="tw-absolute tw-right-0 tw-top-0 tw-bottom-0 tw-w-24 tw-pointer-events-none tw-z-10 tw-bg-gradient-to-l tw-from-black tw-via-black/40 tw-to-black/0" /><button onClick={scrollRight} aria-label="Scroll tabs right" className="tw-absolute tw-right-0 tw-top-1/2 tw--translate-y-1/2 tw-z-20 tw-inline-flex tw-items-center tw-justify-end tw-group tw-p-0 tw-h-10 tw-w-10 tw-bg-transparent tw-border-none tw-outline-none"><FontAwesomeIcon icon={faChevronRight} className="tw-h-6 tw-w-6 tw-text-iron-200 group-hover:tw-text-iron-300 tw-transition tw-duration-300 tw-ease-out" /></button></>;
    $[48] = canScrollRight;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  let t20;
  if ($[50] !== t17 || $[51] !== t18 || $[52] !== t19) {
    t20 = <div className="tw-relative tw-overflow-hidden tw-border-b tw-border-iron-700 tw-border-solid tw-border-x-0 tw-border-t-0">{t17}{t18}{t19}</div>;
    $[50] = t17;
    $[51] = t18;
    $[52] = t19;
    $[53] = t20;
  } else {
    t20 = $[53];
  }
  return t20;
}